import React, { useCallback, useEffect, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import { Col, Row, FormGroup } from "react-bootstrap";
import { Controller, useForm, useWatch } from "react-hook-form";
import Selector from "../../../../../components/selector";
import { useAccommodationsQuery } from "../../../../accommodation/accommodation.hook";
import { messages } from "./messages";
import { useOccupationsQuery } from "../../../../occupation/application/hooks/useOccupationsQuery";
import { InventoryEntry } from "../../../../../lib/model";
import {
  useMealPlanApi,
  MealPlanObject,
} from "../../../../batchInventory/infrastructure/api/mealplans.api";
import { useConditionService } from "../../../../witadmin/CRUD/Conditions/hook/useConditionService";
import { Condition } from "../../../../witadmin/CRUD/Conditions/domain/Condition.entity";

export type Option = { value: string; label: string };

type SmartAccommodationProps = {
  locale: string;
  propertyTicker: string;
  ticker: string;
  onChange: (options: Option) => void;
  inventories?: InventoryEntry[];
};

type SmartAccommodationFormProps = {
  accommodations: { ticker: string; translation: string }[];
  combinations: { ticker: string; translation: string }[];
  conditions: string[];
  mealPlans: string[];
  accommodationSelected: Option;
  occupationSelected: Option;
  configurationSelected: Option;
  conditionSelected: Option;
  mealPlanSelected: Option;
};

export function SmartAccommodation(props: SmartAccommodationProps) {
  const { getAllMealPlans } = useMealPlanApi();
  const conditionService = useConditionService();

  const { data: accommodationList = [] } = useAccommodationsQuery(props.propertyTicker);
  const { data: occupationData = [] } = useOccupationsQuery(props.propertyTicker);
  const [mealPlanData, setMealPlanData] = React.useState<MealPlanObject[]>([]);
  const [conditionData, setConditionData] = React.useState<Condition[]>([]);

  const [accommodation, occupation, mealPlan, condition] = props.ticker.split("_");

  // Group virtual inventories by accommodation ticker
  const virtualInventoryMap = useMemo(() => {
    if (!props.inventories) return {};

    const map: Record<string, string[]> = {};

    props.inventories.forEach(inventory => {
      const accommodationTicker = inventory.accommodation.ticker;
      const virtualTickers = inventory.virtualInventory?.map(it => it.ticker) || [];

      if (!map[accommodationTicker]) {
        map[accommodationTicker] = [];
      }

      map[accommodationTicker].push(...virtualTickers);
    });

    return map;
  }, [props.inventories]);

  // Extract unique options for each level based on selection
  const getUniqueOptions = useCallback(
    (accommodationTicker?: string, occupationTicker?: string, mealPlanTicker?: string) => {
      const accommodations = new Set<string>();
      const occupations = new Set<string>();
      const mealPlans = new Set<string>();
      const conditions = new Set<string>();

      Object.entries(virtualInventoryMap).forEach(([previousTicker, tickers]) => {
        accommodations.add(previousTicker);

        tickers.forEach(ticker => {
          const [accommodation, occupation, mealPlan, condition] = ticker.split("_");

          // Filter based on previous selections
          if (!accommodationTicker || accommodation === accommodationTicker) {
            occupations.add(occupation);
            if (!occupationTicker || occupation === occupationTicker) {
              mealPlans.add(mealPlan);
              if (!mealPlanTicker || mealPlan === mealPlanTicker) {
                conditions.add(condition);
              }
            }
          }
        });
      });

      return {
        accommodations: Array.from(accommodations),
        occupations: Array.from(occupations),
        mealPlans: Array.from(mealPlans),
        conditions: Array.from(conditions),
      };
    },
    [virtualInventoryMap]
  );

  const methods = useForm<SmartAccommodationFormProps>({
    mode: "onChange",
    defaultValues: {
      accommodationSelected: { value: accommodation, label: accommodation },
      occupationSelected: { value: occupation, label: occupation },
      conditionSelected: { value: condition, label: condition },
      mealPlanSelected: { value: mealPlan, label: mealPlan },
      accommodations: [],
      combinations: [],
      conditions: [],
      mealPlans: [],
    },
  });

  const accommodationSelected = useWatch({
    control: methods.control,
    name: "accommodationSelected",
  });
  const occupationSelected = useWatch({ control: methods.control, name: "occupationSelected" });
  const conditionSelected = useWatch({ control: methods.control, name: "conditionSelected" });
  const mealPlanSelected = useWatch({ control: methods.control, name: "mealPlanSelected" });

  // Get available options based on current selections
  const availableOptions = useMemo(() => {
    return getUniqueOptions(
      accommodationSelected?.value,
      occupationSelected?.value,
      mealPlanSelected?.value
    );
  }, [getUniqueOptions, accommodationSelected, occupationSelected, mealPlanSelected]);

  // Create options with translations
  const accommodationOptions = useMemo(() => {
    return availableOptions.accommodations.map(ticker => {
      const accommodation = accommodationList.find(it => it.ticker === ticker);
      const translation =
        accommodation?.translationsList.find(({ locale }) => locale === props.locale)?.name ||
        ticker;
      return { value: ticker, label: translation };
    });
  }, [availableOptions.accommodations, accommodationList, props.locale]);

  const occupationOptions = useMemo(() => {
    return availableOptions.occupations.map(ticker => {
      const occupation = occupationData.find(it => it.ticker === ticker);
      const translation =
        occupation?.translationsList.find(({ locale }) => locale === props.locale)?.name || ticker;
      return { value: ticker, label: translation };
    });
  }, [availableOptions.occupations, occupationData, props.locale]);

  const mealPlanOptions = useMemo(() => {
    return availableOptions.mealPlans.map(ticker => {
      const mealPlan = mealPlanData.find(it => it.ticker === ticker);
      const translation =
        mealPlan?.translationsList.find(({ locale }) => locale === props.locale)?.name || ticker;
      return { value: ticker, label: translation };
    });
  }, [availableOptions.mealPlans, mealPlanData, props.locale]);

  const conditionOptions = useMemo(() => {
    return availableOptions.conditions.map(ticker => {
      const condition = conditionData.find(it => it.ticker === ticker);
      const translation = condition?.getTranslation(props.locale).name || ticker;
      return { value: ticker, label: translation };
    });
  }, [availableOptions.conditions, conditionData, props.locale]);

  const allInputSelected = useMemo(() => {
    return (
      accommodationSelected?.value &&
      occupationSelected?.value &&
      conditionSelected?.value &&
      mealPlanSelected?.value
    );
  }, [accommodationSelected, occupationSelected, conditionSelected, mealPlanSelected]);

  const handleChangeAccommodation = useCallback(
    (option: Option) => {
      methods.setValue("accommodationSelected", option);
      methods.setValue("occupationSelected", { value: "", label: "" });
      methods.setValue("mealPlanSelected", { value: "", label: "" });
      methods.setValue("conditionSelected", { value: "", label: "" });
    },
    [methods]
  );

  const handleChangeOccupation = useCallback(
    (option: Option) => {
      methods.setValue("occupationSelected", option);
      methods.setValue("mealPlanSelected", { value: "", label: "" });
      methods.setValue("conditionSelected", { value: "", label: "" });
    },
    [methods]
  );

  const handleChangeMealPlan = useCallback(
    (option: Option) => {
      methods.setValue("mealPlanSelected", option);
      methods.setValue("conditionSelected", { value: "", label: "" });
    },
    [methods]
  );

  const handleChangeCondition = useCallback(
    (option: Option) => {
      methods.setValue("conditionSelected", option);
    },
    [methods]
  );

  useEffect(() => {
    getAllMealPlans(props.propertyTicker).then(setMealPlanData);
  }, [getAllMealPlans, props.propertyTicker]);

  useEffect(() => {
    conditionService.getAll(props.propertyTicker).then(setConditionData);
  }, [conditionService, props.propertyTicker]);

  // Update accommodation selection with translation once data is loaded
  useEffect(() => {
    if (accommodationList.length > 0 && accommodation) {
      const acc = accommodationList.find(it => it.ticker === accommodation);
      const translation =
        acc?.translationsList.find(({ locale }) => locale === props.locale)?.name || accommodation;
      methods.setValue("accommodationSelected", { value: accommodation, label: translation });
    }
  }, [accommodationList, accommodation, props.locale, methods]);

  // Update occupation selection with translation once data is loaded
  useEffect(() => {
    if (occupationData.length > 0 && occupation) {
      const occ = occupationData.find(it => it.ticker === occupation);
      const translation =
        occ?.translationsList.find(({ locale }) => locale === props.locale)?.name || occupation;
      methods.setValue("occupationSelected", { value: occupation, label: translation });
    }
  }, [occupationData, occupation, props.locale, methods]);

  // Update mealPlan selection with translation once data is loaded
  useEffect(() => {
    if (mealPlanData.length > 0 && mealPlan) {
      const mp = mealPlanData.find(it => it.ticker === mealPlan);
      const translation =
        mp?.translationsList.find(({ locale }) => locale === props.locale)?.name || mealPlan;
      methods.setValue("mealPlanSelected", { value: mealPlan, label: translation });
    }
  }, [mealPlanData, mealPlan, props.locale, methods]);

  // Update condition selection with translation once data is loaded
  useEffect(() => {
    if (conditionData.length > 0 && condition) {
      const cond = conditionData.find(it => it.ticker === condition);
      const translation = cond?.getTranslation(props.locale).name || condition;
      methods.setValue("conditionSelected", { value: condition, label: translation });
    }
  }, [conditionData, condition, props.locale, methods]);

  useEffect(() => {
    if (allInputSelected) {
      const newTicker = `${accommodationSelected.value}_${occupationSelected.value}_${mealPlanSelected.value}_${conditionSelected.value}`;
      props.onChange({ value: newTicker, label: newTicker });
    }
  }, [
    accommodationSelected.value,
    allInputSelected,
    occupationSelected.value,
    conditionSelected.value,
    mealPlanSelected.value,
    props,
  ]);

  return (
    <FormGroup>
      <Row sm={12} className="mb-3">
        <Col sm={3}>
          <Controller
            name="accommodationSelected"
            control={methods.control}
            render={({ field }) => (
              <Selector
                options={accommodationOptions}
                placeholder={<FormattedMessage {...messages.accommodationPlaceholder} />}
                required
                {...field}
                onChange={handleChangeAccommodation}
              />
            )}
          />
        </Col>
        <Col sm={3}>
          <Controller
            name="occupationSelected"
            control={methods.control}
            render={({ field }) => (
              <Selector
                options={occupationOptions}
                placeholder={<FormattedMessage {...messages.occupationPlaceholder} />}
                required
                isDisabled={!accommodationSelected?.value}
                {...field}
                onChange={handleChangeOccupation}
              />
            )}
          />
        </Col>
        <Col sm={3}>
          <Controller
            name="mealPlanSelected"
            control={methods.control}
            render={({ field }) => (
              <Selector
                options={mealPlanOptions}
                placeholder={<FormattedMessage {...messages.mealPlanPlaceholder} />}
                required
                isDisabled={!occupationSelected?.value}
                {...field}
                onChange={handleChangeMealPlan}
              />
            )}
          />
        </Col>
        <Col sm={3}>
          <Controller
            name="conditionSelected"
            control={methods.control}
            render={({ field }) => (
              <Selector
                options={conditionOptions}
                placeholder={<FormattedMessage {...messages.conditionPlaceholder} />}
                required
                isDisabled={!mealPlanSelected?.value}
                {...field}
                onChange={handleChangeCondition}
              />
            )}
          />
        </Col>
      </Row>
    </FormGroup>
  );
}
